import { subscribe, THEME_EVENTS } from '@theme/events';

/**
 * The subscriber `cart:error` never had.
 *
 * Four sites published it — product-form's add rejection and its network catch,
 * the drawer's line-change rejection — and nothing in the theme listened, so
 * every cart failure was discarded. Shopify hands back a written reason in the
 * shop's own language ("Produkten … är redan slutsåld."); this shows it.
 *
 * Not scoped to a section: an add fails on the product page while the drawer is
 * shut, and a line change fails while it is open. One listener covers both,
 * because the element lives in the layout and its popover puts it in the top
 * layer either way.
 */
class CartToast extends HTMLElement {
  connectedCallback() {
    try {
      this.message = this.querySelector('[data-element="cart-toast-message"]');
      this.unsubscribe = subscribe(THEME_EVENTS.cartError, ({ message, source }) => this.show(message, source));
    } catch (error) {
      console.error('cart toast init failed', error);
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    clearTimeout(this.timer);
  }

  // Only an ADD quotes the server.
  //
  // Shopify localises the errors a shopper can act on and returns them in the
  // shop's language — "Produkten … är redan slutsåld." names the product and
  // the reason, which no generic string can, so it is worth repeating verbatim.
  //
  // A line CHANGE is different. Its failures are reconciliation problems the
  // shopper neither caused nor can fix, and Shopify answers them with internal
  // English: a stale line key returns "Cannot find variant", which is developer
  // text shown to a Swedish shopper. Measured on the dev store. The drawer
  // resyncs to what the cart really holds either way, so the honest message is
  // the generic one — the specific one would only mislead.
  static QUOTES_SERVER = new Set(['add']);

  show(message, source) {
    const quoted = CartToast.QUOTES_SERVER.has(source) ? String(message ?? '').trim() : '';
    const text = quoted || this.dataset.fallback || '';
    if (!text || !this.message) return;

    // Re-announce a REPEATED message: a shopper tapping a dead add button twice
    // gets the same sentence, and a live region ignores an unchanged value. The
    // empty write flushes it so the second tap is spoken too.
    this.message.textContent = '';
    this.message.textContent = text;

    clearTimeout(this.timer);
    // showPopover() throws if it is already open, which the second tap would do.
    if (!this.matches(':popover-open')) this.showPopover?.();
    // Long enough to read a sentence at a glance, short enough not to sit over
    // the drawer's controls. Nothing is lost when it goes: the cart itself is
    // the record of what happened.
    this.timer = setTimeout(() => this.hidePopover?.(), 6000);
  }
}

if (!customElements.get('cart-toast')) customElements.define('cart-toast', CartToast);
